import {
  createContext,
  ReactChild,
  ReactChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { IOrder, IOrderContext } from '../typing/order';
import { useProfile } from './profile';
import axiosInstance from '../utils/axiosInstance';
import { GET_ORDERS_URL } from '../utils/constants';

const OrderContext = createContext<IOrderContext>({
  orders: [] as IOrder[],
  setOrders: () => {},
  page: 1,
  setPage: () => {},
  limit: 5,
  setLimit: () => {},
  loading: false,
  total: 0,
  profileError: false,
});

export const useOrder = () => useContext(OrderContext);

interface OrderProviderProps {
  children: ReactChildren | ReactChild;
}

export default function OrderProvider({ children }: OrderProviderProps) {
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(5);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [profileError, setProfileError] = useState(false);
  const { activeProfile } = useProfile();

  const getOrders = useCallback(
    async (page: number) => {
      if (!activeProfile) return;

      await axiosInstance
        .get(GET_ORDERS_URL, {
          params: {
            mrn: activeProfile?.mrn,
            facilityId:
              activeProfile?.facilityInfo?.id || activeProfile?.facilityId,
            phoneNo: activeProfile?.phoneNo,
            limit: 5,
            page: page,
          },
        })
        .then((result) => {
          const data = result.data.result;
          const newOrders: IOrder[] = [];
          for (const order of data.orders) {
            if (order as IOrder) {
              newOrders.push(order);
            }
          }
          if (page !== 1) {
            setOrders((orders) => [...orders, ...newOrders]);
          } else {
            setOrders(newOrders);
          }
          setTotal(data.total);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          setProfileError(true);
        });
    },
    [activeProfile]
  );

  useEffect(() => {
    setProfileError(false);
  }, [getOrders]);

  useEffect(() => {
    setLoading(true);
    setPage(1);
    getOrders(1);
  }, [getOrders, activeProfile]);

  useEffect(() => {
    if (page === 1) return;
    setLoading(true);
    getOrders(page);
  }, [getOrders, page, limit]);

  return (
    <OrderContext.Provider
      value={{
        orders,
        setOrders,
        page,
        setPage,
        limit,
        setLimit,
        loading,
        total,
        profileError,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
}
